import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import ActionsMenu from '../../../components/shared/ActionsMenu'
import { useServiceContext } from '../../../root'
import ModalRemove from './Actions/ModalRemove'
import type { Watermark } from '../../../services/api/watermarks'

interface TableMenuProps {
  item: Watermark
  onEditWatermark: (watermark: Watermark) => void
  onDeleteSuccess?: () => void
  onExportStart?: () => void
  onExportEnd?: () => void
}

interface MenuItem {
  value: string
  action: () => void
  disabled?: boolean
}

const TableMenu: React.FC<TableMenuProps> = ({
  item,
  onEditWatermark,
  onDeleteSuccess,
  onExportStart,
  onExportEnd
}) => {
  const context = useServiceContext()
  const navigate = useNavigate()
  const { translate } = context.service.filter
  const { toast } = context.service
  const [isOpen, setIsOpen] = useState(false)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [items, setItems] = useState<MenuItem[]>([
    {
      value: 'LOADING...',
      action: () => {},
      disabled: true
    }
  ])

  const handleExport = async () => {
    try {
      // Start loading
      if (onExportStart) {
        onExportStart()
      }

      // Export single watermark with entityId parameter
      await context.api.settings.exportSettingsData('WATERMARKS', undefined, item.id, translate)
    } catch (error) {
      toast.error(translate('SETTINGS_IMPORT_FAIL'))
    } finally {
      // End loading
      if (onExportEnd) {
        onExportEnd()
      }
    }
  }

  const handleActivity = () => {
    // Navigate to settings activity page with watermark parameters
    const params = new URLSearchParams({
      id: item.id.toString(),
      type: 'WATERMARK',
      itemName: item.name
    })
    navigate(`/settings/activity?${params.toString()}`)
  }

  const setOptions = (watermark: Watermark) => {
    const menu: MenuItem[] = []

    menu.push({
      value: 'ACTION_EDIT',
      action: () => {
        onEditWatermark(watermark)
      }
    })

    menu.push({
      value: 'ACTION_REMOVE',
      action: () => {
        setShowRemoveModal(true)
      }
    })

    menu.push({
      value: 'ACTION_EXPORT',
      action: handleExport
    })

    menu.push({
      value: 'BROWSER_ACTION_ACTIVITY',
      action: handleActivity
    })

    setItems(menu)
  }

  useEffect(() => {
    setOptions(item)
  }, [item, isOpen])

  return (
    <>
      <ActionsMenu
        menuId={`action-menu-watermark-${item.id}`}
        onToggle={(open) => {
          setIsOpen(open)
        }}
        items={items}
      />
      {showRemoveModal && (
        <ModalRemove
          watermark={item}
          onClose={() => setShowRemoveModal(false)}
          onSuccess={() => {
            setShowRemoveModal(false)
            if (onDeleteSuccess) {
              onDeleteSuccess()
            }
          }}
        />
      )}
    </>
  )
}

export default TableMenu
